import { Controller } from 'stimulus';
import { Tabulator } from 'tabulator-tables';
import { t } from '../javascript/lib/i18n';
import { tabulatorLangs } from '../javascript/lib/library_locales';

// Side-effect imports: register Tabulator's modules and extend its sort /
// layout modules. `sorters.js` and `layout.js` already `import './register'`
// themselves so the registration is technically transitive — listing it
// here too is just so a reader of this file sees the load order at a
// glance without having to chase the side-effect chain through siblings.
import './tabulator_table/register';
import {
  enhanceSortButtons,
  refreshSortLabels,
} from './tabulator_table/sorters';
import './tabulator_table/layout';
import {
  installTreeKeyboardWatchdog,
  treeRowFormatter,
  treeCollapseButtonHtml,
  treeExpandButtonHtml,
} from './tabulator_table/tree';

import {
  buildFilterColumnOptions,
  isFilterActive,
  liftFilterButtons,
  matchesFilter,
  matchesGlobalSearch,
  normaliseFilterOptions,
  refreshFilterButtonStates,
  setFilterButtonExpanded,
  syncGroupVisibility,
} from './tabulator_table/filters';
import {
  closeColumnSearchPopup,
  installColumnSearchButtons,
  refreshColumnSearchButtonStates,
} from './tabulator_table/column_search';
import {
  extractCellText,
  slugify,
  todayFilenameDate,
} from './tabulator_table/helpers';
import { StickyHorizontalScrollbar } from './tabulator_table/horizontal_scrollbar';
import {
  csvDetailRows,
  firstColumnField,
  withGroupColumn,
} from './tabulator_table/csv_group_split';
import {
  deserializeFilterState,
  loadTableState,
  saveTableState,
  serializeFilterState,
} from './tabulator_table/persistence';

// Tabulator clears the error alert after this delay (its default is 3s), which
// drops the table back to its "no results" placeholder — the reading the error
// state exists to prevent. Hold it until the next load, which replaces it.
const DATA_LOAD_ERROR_TIMEOUT_MS = 60 * 60 * 1000;

// Return a shallow copy of the column config minus the keys we manage
// ourselves (e.g. `maxWidth`, `filter`, `sortField`). Tabulator warns
// "Invalid column definition option" for keys it doesn't recognise, so
// anything we re-shape or consume ourselves has to be stripped before
// forwarding `...rest` into Tabulator's column definition.
function stripColumnKeys(col, keysToStrip) {
  return Object.fromEntries(
    Object.entries(col).filter(([k]) => !keysToStrip.includes(k))
  );
}

export default class extends Controller {
  static targets = [
    'container',
    'source',
    'search',
    'clear',
    'loader',
    'groupHeaders',
  ];

  static values = {
    columns: Array,
    pagination: Boolean,
    pageSize: Number,
    verticalCentreCells: Boolean,
    // Opt-out for the always-visible proxy scrollbar (see
    // `installStickyHorizontalScrollbar`). Wide tables inside a modal keep
    // native horizontal scrolling instead.
    stickyHorizontalScrollbar: { type: Boolean, default: true },
    emptyMessage: String,
    // Remote mode: when true, the table fetches each page from a JSON
    // endpoint instead of reading rows from the source `<table>`.
    remote: Boolean,
    // Optional explicit endpoint path for remote mode. Empty string
    // defers to the current page path with `.json` appended — same
    // controller serving both HTML and JSON, no extra wiring at the
    // call site. Path only; host/scheme come from `window.location.origin`.
    ajaxPath: String,
    // Initial sort in ransack `"field dir"` shape (e.g. "closed_at desc").
    // Local mode uses it to pre-sort; remote mode forwards it to the
    // server in the first request's `q[s][]`.
    defaultSort: String,
    // Local mode only: when set, a CSV download also POSTs here so the
    // client-side export can be recorded as an ExportAudit. The path is
    // expected to already carry the export context (date range etc.).
    csvAuditPath: String,
    // Extra URL param names that signal "the user arrived via a deep
    // link" beyond the per-column `deepLinkKey`s. These are filters the
    // server applies on its own (no matching column / popup), so the JS
    // never seeds or strips them — they just need to count toward
    // `urlHasDeepLinkParams` so persisted session filters don't get
    // merged on top of an explicit drill-through (e.g. `?closed_by_user=true`
    // from the Overview totals, which maps to no column).
    deepLinkKeys: Array,
    // CSV-only group split (see the component's `csv_group_header`).
    // When the group header is non-empty, the download gains a leading hidden
    // column naming each row's group, and the first column is retitled to the
    // detail header for download. See csv_group_split.js.
    csvGroupHeader: String,
    csvDetailHeader: String,
    // Row grouping (local mode): nest rows under a header per logical group,
    // reusing the `data-row-group` each source row already declares.
    groupedRows: Boolean,
    // Every group value, in display order. Values with no rows still render a
    // header, which is how an empty group stays visible and manageable.
    groupOrder: Array,
    // Grouped tables: hide a group's header while a filter leaves it no rows.
    hideEmptyGroups: Boolean,
    // Row reordering (local mode): rows can be dragged by the column that
    // declares `rowHandle`. Persisting is the screen's job — we emit
    // `tabulator-table:row-moved` carrying the complete new grouped order.
    movableRows: Boolean,
  };

  connect() {
    this.initTable();
    // When the table's heading/toolbar lives in a separate shell (org-group
    // dashboards render it outside the table's turbo frame), accept clear and
    // download-CSV requests via window events scoped to this frame.
    this.onClearRequest = this.onClearRequest.bind(this);
    window.addEventListener('tabulator-table:clear', this.onClearRequest);
    // A `<details>` inside a cell (see tabulator_details_controller) bubbles
    // this event on toggle so we can re-measure the row — Tabulator sizes rows
    // once at render and would otherwise clip the disclosed content.
    this.onCellContentToggled = this.onCellContentToggled.bind(this);
    this.element.addEventListener(
      'tabulator-table:cell-content-toggled',
      this.onCellContentToggled
    );
    this.onDownloadCsvRequest = this.onDownloadCsvRequest.bind(this);
    window.addEventListener(
      'tabulator-table:download-csv',
      this.onDownloadCsvRequest
    );
  }

  disconnect() {
    window.removeEventListener('tabulator-table:clear', this.onClearRequest);
    this.element.removeEventListener(
      'tabulator-table:cell-content-toggled',
      this.onCellContentToggled
    );
    window.removeEventListener(
      'tabulator-table:download-csv',
      this.onDownloadCsvRequest
    );
    // Remove any open column-search popup + its document/popup listeners
    // so nothing leaks past the controller's lifetime.
    closeColumnSearchPopup(this);
    // Cancel any pending debounced refetch — otherwise a keystroke made
    // within the debounce window before a Turbo navigation fires
    // `applyFilters` after `this.tabulator` has been destroyed below
    // (remote mode → `this.tabulator.setPage(1)` on null → TypeError).
    clearTimeout(this._applyFiltersTimer);
    if (this._onExternalRefresh) {
      this.element.removeEventListener(
        'tabulator-table:refresh',
        this._onExternalRefresh
      );
      this._onExternalRefresh = null;
    }
    if (this._onSyncGroup) {
      this.element.removeEventListener(
        'tabulator-table:sync-group',
        this._onSyncGroup
      );
      this._onSyncGroup = null;
    }
    if (this._onSyncGroups) {
      this.element.removeEventListener(
        'tabulator-table:sync-groups',
        this._onSyncGroups
      );
      this._onSyncGroups = null;
    }
    if (this._onResyncRows) {
      this.element.removeEventListener(
        'tabulator-table:resync-rows',
        this._onResyncRows
      );
      this._onResyncRows = null;
    }
    if (this._onSetExternalFilter) {
      this.element.removeEventListener(
        'tabulator-table:set-external-filter',
        this._onSetExternalFilter
      );
      this._onSetExternalFilter = null;
    }
    if (this._onRowMoveEnd) {
      document.removeEventListener('mouseup', this._onRowMoveEnd);
      this._onRowMoveEnd = null;
    }
    this._groupHeaderObserver?.disconnect();
    this._groupHeaderObserver = null;
    this._parkGroupHeaders();
    this._groupHeaders?.clear();
    if (this.horizontalScrollbar) {
      this.horizontalScrollbar.destroy();
      this.horizontalScrollbar = null;
    }
    if (this.tabulator) {
      this.tabulator.destroy();
      this.tabulator = null;
    }
  }

  // The turbo-frame id this table loads inside, if any. Used to scope the
  // cross-frame toolbar events so multiple framed tables don't cross-talk.
  get frameId() {
    return this.element.closest('turbo-frame')?.id ?? null;
  }

  onClearRequest(event) {
    if (event.detail?.frame === this.frameId) this.clear();
  }

  // Re-measure the row whose in-cell `<details>` just toggled. Tabulator
  // computes row heights at render time and doesn't watch for later content
  // changes, so `normalizeHeight()` re-reads the row's rendered height and
  // `adjustTableSize()` reflows the holder around the new total. Neither
  // regenerates cell HTML, so the `<details>` keeps its open/closed state.
  onCellContentToggled(event) {
    if (!this.tabulator) return;
    const rowEl = event.target.closest?.('.tabulator-row');
    if (!rowEl) return;
    const rowComponent = this.tabulator
      .getRows()
      .find((row) => row.getElement() === rowEl);
    if (!rowComponent) return;
    rowComponent.normalizeHeight();
    this.tabulator.rowManager.adjustTableSize();
  }

  onDownloadCsvRequest(event) {
    if (event.detail?.frame === this.frameId) this.downloadCsv();
  }

  initTable() {
    // Source `<table>` only exists in local mode (it's the fallback the
    // component renders for no-JS users + the data source we reflow
    // into Tabulator). Remote mode has no source — the JS fetches the
    // first page on connect.
    if (this.hasSourceTarget) this.sourceTarget.classList.add('d-none');
    // Per-column filter state — `field → { kind, ... }`. Lives on the
    // controller so it survives the popup being closed/re-opened. The
    // popup builders (`buildCheckboxFilterPopup`, `buildNumberFilterPopup`)
    // seed kind-specific shapes via `getOrInitFilterState`.
    this.filterState = new Map();
    // Group value → its server-rendered header node. `groupHeaderFor` prefers a
    // fresh query (so a turbo_stream replacement wins) and falls back to this
    // map for the moment mid-regeneration when Tabulator has detached the node.
    this._groupHeaders = new Map();
    this._observeGroupHeaders();
    // Seed from URL deep-link params BEFORE constructing Tabulator —
    // remote mode fires its first AJAX request from the constructor, and
    // `buildAjaxUrl` reads `filterState` to emit ransack q-params. Seeding
    // after construction would leave the first request unfiltered, since
    // `buildAjaxUrl` also strips the raw deep-link keys from the URL.
    this.seedFilterStateFromUrl();
    // Remote mode: pre-seed persisted state into the config so the
    // constructor's single initial request already carries the restored
    // sort, page, filter and search — no post-construction setSort/setPage
    // calls that would each trigger an extra fetch.
    if (this.remoteValue) this.restoreStateIntoConfig();
    // Gate persistence writes until the initial restore completes. Tabulator
    // fires `dataSorted` / `pageLoaded` while we're applying restored state,
    // and we don't want those echoes overwriting storage with the same
    // snapshot we just read.
    this._restoring = true;
    this.tabulator = new Tabulator(this.containerTarget, this.config);

    this.tabulator.on('tableBuilt', () => {
      liftFilterButtons(this.containerTarget);
      enhanceSortButtons(this.containerTarget);
      const searchButtonsInserted = installColumnSearchButtons(
        this.containerTarget,
        this
      );
      // The search button is appended after Tabulator's initial width
      // measurement, so its (fixed 20px) width isn't reflected in the
      // content-sized column width. Re-run the layout so the column is
      // re-measured with the button present — otherwise a table with no rows
      // to widen the column leaves the header too narrow and the sort arrow
      // and search icon overlap. `reinitializeWidth` re-reads the header's
      // `offsetWidth` (now including the button), so this composes with every
      // width option: explicit `width:` is preserved, `contentCap`/`maxWidth`
      // still caps the measured content, and `widthGrow` still distributes
      // slack afterwards. The native filter icon needs no equivalent — it's
      // measured in the first pass because Tabulator renders it itself.
      if (searchButtonsInserted > 0) this.tabulator.redraw();
      installTreeKeyboardWatchdog(this.containerTarget);
      this.installFilterPopupScrollGuard();
      if (this.stickyHorizontalScrollbarValue) {
        this.installStickyHorizontalScrollbar();
      }
      // Mark the wrapper as "JS has actually rendered the table" so SCSS
      // can scope no-JS-incompatible layout rules.
      this.element.classList.add('amba-tabulator-initialised');
      this.restoreState();
      // `restoreState` only refreshes button states when persisted state
      // exists; the URL-seeded filterState set before construction still
      // needs its visuals applied on the no-persistence path.
      refreshFilterButtonStates(this.containerTarget, this.filterState);
      refreshColumnSearchButtonStates(this.containerTarget, this.filterState);
      this.dispatch('built');
    });
    this.tabulator.on('dataSorted', () => {
      refreshSortLabels(this.containerTarget);
      this.refreshClearButtonVisibility();
      this.saveState();
    });
    this.tabulator.on('pageLoaded', () => this.saveState());
    if (this.remoteValue) {
      this.installHorizontalScrollPreservation();
      // Listen for external refresh requests (e.g. date range / floor
      // filter changes wrapped in a `tabulator-table-refresher` container).
      // `buildAjaxUrl` always reads from `window.location.href`, so the
      // dispatcher just updates the URL via `history.pushState` and fires
      // this event; we re-page from 1 and Tabulator's existing AJAX
      // pipeline picks up the new params. Sort / filter / search state
      // on the controller is untouched.
      this._onExternalRefresh = () => this.tabulator.setPage(1);
      this.element.addEventListener(
        'tabulator-table:refresh',
        this._onExternalRefresh
      );
    }
    this.tabulator.on('popupOpened', (component) => {
      setFilterButtonExpanded(component, true);
      this._filterPopupOpen = true;
    });
    this.tabulator.on('popupClosed', (component) => {
      setFilterButtonExpanded(component, false);
      this._filterPopupOpen = false;
    });
    // Surgical external refresh (local mode / row-index opt-in): a
    // turbo_stream that swapped a single row group's <tbody> in the hidden
    // source table dispatches this so we reconcile just those rows into the
    // live table without a full re-mount. See `syncRowGroup`.
    this._onSyncGroup = (event) => this.syncRowGroup(event.detail?.group);
    this.element.addEventListener(
      'tabulator-table:sync-group',
      this._onSyncGroup
    );
    // Grouped tables only: a stream that added or removed a whole group
    // redeclares the group set before its rows are synced (a row whose group
    // isn't declared yet is silently dropped by Tabulator).
    this._onSyncGroups = (event) =>
      this.setGroupOrder(event.detail?.groupOrder);
    this.element.addEventListener(
      'tabulator-table:sync-groups',
      this._onSyncGroups
    );
    // Undo a drag the screen could not persist. The source table is untouched by
    // a move, so re-reading it *is* the persisted order.
    this._onResyncRows = () => this.resyncRows();
    this.element.addEventListener(
      'tabulator-table:resync-rows',
      this._onResyncRows
    );
    // External filter: a toolbar control (e.g. a role-filter dropdown) can
    // drive a hidden column's checkbox filter by dispatching this event with
    // `{field, kind, criteria}`. Generic so any hidden-column filter can be
    // triggered from outside the header.
    this._onSetExternalFilter = (event) => {
      const { field, kind, criteria } = event.detail || {};
      if (!field || !kind || !criteria) return;
      this.setFilterCriteria(field, kind, criteria);
      this.applyFilters();
    };
    this.element.addEventListener(
      'tabulator-table:set-external-filter',
      this._onSetExternalFilter
    );
    if (this.movableRowsValue) this.installRowMoveEvents();
  }

  // A finished drag emits the complete new order for the screen to persist.
  //
  // It also guards the one way Tabulator can lie about a drag: `endMove` swaps
  // the dragged row into the placeholder's slot *before* it calls `moveRow`, so
  // if anything removes the floating hover clone mid-drag (a redraw from the
  // container resizing does) the `removeChild` between those two lines throws
  // and the row is left looking moved while nothing was recorded or saved.
  // Neither `rowMoved` nor `rowMoveCancelled` fires in that case, so a drag that
  // ends without either is put back to the persisted order.
  installRowMoveEvents() {
    this.tabulator.on('rowMoving', () => {
      this._rowMoveHandled = false;
      this._onRowMoveEnd = () =>
        setTimeout(() => {
          if (!this._rowMoveHandled) this.resyncRows();
        }, 0);
      document.addEventListener('mouseup', this._onRowMoveEnd, { once: true });
    });
    this.tabulator.on('rowMoveCancelled', () => {
      this._rowMoveHandled = true;
    });
    this.tabulator.on('rowMoved', (row) => {
      this._rowMoveHandled = true;
      this.dispatch('row-moved', {
        detail: { groupedOrder: this.groupedOrder(row) },
      });
    });
  }

  // Re-read the source table and put the live table back to it, keeping the
  // current filters and search. Local mode only — a remote table has no source.
  resyncRows() {
    if (this.remoteValue || !this.tabulator) return Promise.resolve();

    return this.tabulator
      .replaceData(this.extractRowData())
      .then(() => this.applyFilters())
      .catch(() => {});
  }

  // The complete new order after a drag, as `{ groupKey: [rowId, …] }`.
  //
  // Derived from `getRows()` — RowManager's own `this.rows`, the whole
  // unfiltered set, which `moveRowActual` reorders in place. Deriving from
  // `getGroups()` / `group.getRows()` instead would emit a *filtered* order,
  // so a drag made with a filter on would tell the server the hidden rows had
  // gone. Ungrouped tables emit a single `''` bucket.
  //
  // A cross-group drag leaves the moved row's own group field stale (Tabulator
  // moves it between groups without touching row data), so take the new key
  // from the group it now belongs to and write it back — every later
  // derivation, and `syncRowGroup`, read that field.
  groupedOrder(movedRow) {
    if (movedRow) {
      const key = movedRow.getGroup?.()?.getKey?.();
      if (key !== undefined && key !== null) {
        movedRow.getData()._rowGroup = String(key);
      }
    }

    const order = {};
    this.tabulator.getRows().forEach((row) => {
      const data = row.getData();
      const group = String(data._rowGroup ?? '');
      order[group] = order[group] || [];
      order[group].push(String(data._rowId));
    });
    return order;
  }

  // Mount the always-visible sticky horizontal scrollbar and keep it in sync
  // as content re-renders. `containerTarget` is the element Tabulator turns
  // into `.tabulator`, so the helper inserts the bar as its sibling (see the
  // helper for why a child won't float). ResizeObserver inside the helper
  // covers most size changes; the explicit `renderComplete` refresh handles
  // re-renders that swap row content without resizing the holder (e.g. remote
  // paging).
  installStickyHorizontalScrollbar() {
    this.horizontalScrollbar = new StickyHorizontalScrollbar(
      () => this.tabulator?.rowManager?.element,
      this.containerTarget,
      // Scroll via Tabulator so the header follows directly — a bare
      // `scrollLeft` write relies on the holder's scroll event, which
      // `installFilterPopupScrollGuard` blocks while a filter popup is open.
      // `rowManager.scrollHorizontal` moves the body; `columnManager` moves the
      // header — Tabulator's own scroll handler calls both, so we mirror it.
      (left) => {
        this.tabulator?.rowManager?.scrollHorizontal(left);
        this.tabulator?.columnManager?.scrollHorizontal(left);
      }
    );
    this.tabulator.on('renderComplete', () => {
      this.horizontalScrollbar?.refresh();
      this.containerTarget.style.minHeight = '';
    });
  }

  // Tabulator's `hideOnBlur` for popups binds a `scroll` listener on the
  // row manager element that auto-closes any open popup. That fires
  // spuriously during filter changes: clicking "Deselect" (or any other
  // filter mutation) shrinks the row content; if the user had scrolled
  // the table, the browser auto-adjusts `scrollTop` and emits a scroll
  // event, which closes the popup mid-interaction. Capture-phase guard
  // here swallows scroll events on the row manager while any filter
  // popup is open. Outside-click + Escape still close the popup, so we
  // lose nothing functional.
  installFilterPopupScrollGuard() {
    const rowEl = this.tabulator.rowManager?.element;
    if (!rowEl) return;
    rowEl.addEventListener(
      'scroll',
      (e) => {
        if (this._filterPopupOpen) e.stopImmediatePropagation();
      },
      true
    );
  }

  // Preserve horizontal scroll across remote refetches. Tabulator's
  // `setData` runs `resetScroll()` before render and fires several render
  // passes per data load (each one re-zeroes scrollLeft), so we capture
  // scrollLeft on `dataLoading` (before resetScroll runs) and re-apply on
  // every `renderComplete`.
  installHorizontalScrollPreservation() {
    this._savedScrollLeft = null;

    this.tabulator.on('dataLoading', () => {
      const rowEl = this.tabulator.rowManager?.element;
      this._savedScrollLeft = rowEl?.scrollLeft || null;
    });

    this.tabulator.on('renderComplete', () => {
      if (!this._savedScrollLeft) return;
      this.tabulator.rowManager.scrollHorizontal(this._savedScrollLeft);
    });
  }

  // Seed `filterState` from URL deep-link query params. Each column's
  // filter config opts in by declaring `deepLinkKey:` — the name of the
  // URL param whose value should be pre-ticked in that column's popup
  // (e.g. `?outcome_id=12` → tick "12" in the outcome filter). Only
  // checkbox filters are seedable; number filters use a different state
  // shape that doesn't map to a simple URL value.
  //
  // Skips values that don't match any popup option (e.g. the "none"
  // sentinel some deep-links use for "no value set"). Those don't map to
  // a checkbox tick, and ransack's `_in` predicate can't represent NULL
  // anyway — so we leave the raw deep-link key on the URL and let the
  // server's filter path handle it.
  seedFilterStateFromUrl() {
    // Deep-link keys we actually seeded into `filterState`. Once a key is
    // here, JS owns that filter for the rest of the session and the raw URL
    // param must be stripped from every request — even after the user clears
    // filters, which empties `filterState` but doesn't mean "fall back to the
    // server-side deep-link filter". Keys we couldn't seed (sentinel values
    // like "none") never land here, so they keep passing through to the
    // server. Tracking seeded keys separately from `filterState` is what lets
    // Clear actually widen the table back to all rows.
    this.seededDeepLinkKeys = new Set();
    const params = new URL(window.location.href).searchParams;
    this.columnsValue.forEach((col) => {
      const filter = col.filter;
      if (!filter || filter.type !== 'checkbox' || !filter.deepLinkKey) return;
      const value = params.get(filter.deepLinkKey);
      if (!value) return;
      const allOptions = normaliseFilterOptions(filter.options ?? []);
      if (!allOptions.some((o) => o.value === String(value))) return;
      this.seededDeepLinkKeys.add(filter.deepLinkKey);
      this.setFilterCriteria(col.field, 'checkbox', {
        kind: 'checkbox',
        checkedValues: new Set([String(value)]),
        allOptions,
      });
    });
  }

  // `filterState` is a nested Map: `field → (kind → criteria)`. A column can
  // carry more than one narrowing tool at once — e.g. a checkbox filter to
  // pin a few known values AND a free-text search to substring-narrow — so
  // each column holds a small map keyed by kind (`checkbox` / `number` /
  // `search`) rather than a single criteria object. `filter:` is one type per
  // column (checkbox XOR number), so in practice a field holds at most a
  // filter criteria plus a search criteria; the matcher ANDs whatever is
  // present.
  fieldCriteriaMap(field) {
    let kinds = this.filterState.get(field);
    if (!kinds) {
      kinds = new Map();
      this.filterState.set(field, kinds);
    }
    return kinds;
  }

  // Set (replace) one kind's criteria for a field, creating the field's map
  // on demand. Used by the URL deep-link seeder and the persistence restore.
  setFilterCriteria(field, kind, criteria) {
    this.fieldCriteriaMap(field).set(kind, criteria);
  }

  // Called by each popup builder the first time the popup is built for a
  // (field, kind). `factory` returns the seed criteria when nothing's stored
  // yet — a thunk rather than a plain object so the popup builders can
  // construct kind-specific shapes (`Set` for checkbox, operator/value for
  // number, query string for search) without this method knowing about any.
  getOrInitFilterState(field, kind, factory) {
    const kinds = this.fieldCriteriaMap(field);
    let criteria = kinds.get(kind);
    if (!criteria) {
      criteria = factory();
      kinds.set(kind, criteria);
    }
    return criteria;
  }

  // Flatten `filterState` to `[field, criteria]` pairs across every kind, so
  // the matcher (local) and ransack-param emitter (remote) can treat each
  // active criteria uniformly regardless of which column it belongs to.
  *eachFilterCriteria() {
    for (const [field, kinds] of this.filterState) {
      for (const criteria of kinds.values()) {
        yield [field, criteria];
      }
    }
  }

  // Push the current filter state into Tabulator via a single function
  // filter. `setFilter` replaces any previous filter, so we always build
  // the whole predicate from `filterState` and the global search input —
  // simpler than juggling per-field filter entries, and robust against
  // columns being added or removed mid-session.
  //
  // Remote mode takes a different path: there are no in-memory rows for
  // a function filter to run against, so we just refresh the chrome and
  // trigger a refetch — `buildAjaxUrl` reads `filterState` and emits
  // `q[<predicate>][]=value` for each column, so the new filters reach
  // the server via the next request.
  applyFilters() {
    if (this.remoteValue) {
      refreshFilterButtonStates(this.containerTarget, this.filterState);
      refreshColumnSearchButtonStates(this.containerTarget, this.filterState);
      this.refreshClearButtonVisibility();
      this.saveState();
      // `setPage(1)` resets to page 1 and triggers Tabulator's remote
      // pagination handler, which re-fetches via `ajaxURLGenerator` →
      // `buildAjaxUrl` → URL with the new filter params.
      this.tabulator.setPage(1);
      return;
    }

    const searchQuery = this.hasSearchTarget
      ? this.searchTarget.value.trim().toLowerCase()
      : '';
    const searchFields = this.searchableFields;
    const criteria = [...this.eachFilterCriteria()];

    this.tabulator.setFilter((rowData) => {
      // Heading rows are decorative section labels inside a children group
      // — they shouldn't drive visibility in either direction. Always-pass
      // is safe because Tabulator's tree render only emits children of a
      // *visible* parent, so a heading is only ever shown when its parent
      // passed the real filter. This also keeps the heading alongside
      // whatever real children survive the filter, instead of appearing
      // alone (matched on its label text) or disappearing because its
      // mostly-empty cells failed a column filter.
      if (rowData._heading) return true;

      if (
        searchQuery &&
        !matchesGlobalSearch(rowData, searchFields, searchQuery)
      ) {
        return false;
      }
      return criteria.every(([field, criterion]) =>
        matchesFilter(rowData[field], criterion)
      );
    });

    if (this.groupedRowsValue) {
      syncGroupVisibility(
        this.tabulator,
        Boolean(searchQuery) || criteria.length > 0,
        this.hideEmptyGroupsValue
      );
    }

    refreshFilterButtonStates(this.containerTarget, this.filterState);
    refreshColumnSearchButtonStates(this.containerTarget, this.filterState);
    this.refreshClearButtonVisibility();
    this.saveState();
  }

  // True when a column filter or the global search would currently exclude
  // rows — i.e. the table is showing less than everything it holds.
  get hasActiveNarrowing() {
    const hasFilter = [...this.eachFilterCriteria()].some(([, criteria]) =>
      isFilterActive(criteria)
    );
    const hasSearch =
      this.hasSearchTarget && this.searchTarget.value.trim() !== '';
    return hasFilter || hasSearch;
  }

  // Wrapper for the search input. Local mode's `setFilter` is
  // in-memory and cheap so we apply immediately on every keystroke —
  // remote mode fires an AJAX per call, so we debounce. The input wiring
  // in the component stays uniform; the right behaviour is picked here.
  applyFiltersDebounced() {
    if (!this.remoteValue) {
      this.applyFilters();
      return;
    }
    clearTimeout(this._applyFiltersTimer);
    this._applyFiltersTimer = setTimeout(() => this.applyFilters(), 200);
  }

  // Reconcile a single logical row group into the live table after a
  // turbo_stream swapped that group's <tbody> in the hidden source table.
  // A "group" is every source row sharing a `data-row-group` value (e.g. all
  // rows for one property on the Alert Recap page); each row is keyed by its
  // `data-row-id` (Tabulator's `index`, see `localConfig`).
  //
  // Local mode + row-index opt-in only — remote tables own their data
  // server-side and have no source <table> to re-read. Re-extracts the (now
  // updated) source rows and, for the target group:
  //   - deletes live rows that no longer exist in the source
  //   - updates existing rows and adds new ones, keyed by `_rowId`
  // Current sort is re-applied so repositioned rows land correctly, and
  // filters/search are re-run so new rows honour any active narrowing.
  syncRowGroup(groupValue) {
    if (this.remoteValue || !this.tabulator) return;
    if (groupValue == null) return;
    const group = String(groupValue);

    const allRows = this.extractRowData();
    const incoming = allRows.filter((row) => String(row._rowGroup) === group);
    const indexConfigured = this.tabulator.options.index === '_rowId';

    // Bail only when the source rows have never carried stable identity AND the
    // table was never configured with one — the table doesn't opt into sync.
    // When `_hasRowIndex` is temporarily false (all rows deleted) but the table
    // was already indexed, we must still reconcile the group.
    if (!this._hasRowIndex && !indexConfigured) return;

    // Two cases need a full replaceData instead of the surgical path:
    //
    // 1. Index mismatch: the table was initialized without `index: '_rowId'`
    //    (e.g. an empty category had no rows with `data-row-id` at boot).
    //    Once rows appear, `updateOrAddData` can't match incoming rows to
    //    existing ones and treats every row as new.
    //
    // 2. Empty group: every row in the group has been removed. Tabulator can
    //    leave orphan DOM elements when the last row in a group is deleted
    //    via `row.delete()` without a full re-render.
    //
    // Both park the group headers first so the rebuild picks up any
    // turbo_stream-replaced headers rather than falling back to the stale
    // `_groupHeaders` cache.
    if ((this._hasRowIndex && !indexConfigured) || incoming.length === 0) {
      if (!indexConfigured) this.tabulator.options.index = '_rowId';
      this._parkGroupHeaders();
      this.containerTarget.style.minHeight = `${this.containerTarget.offsetHeight}px`;
      return this.tabulator
        .replaceData(allRows)
        .then(() => {
          this.applyFilters();
          this.flashRowGroup(group);
          return null;
        })
        .catch(() => {});
    }

    const incomingIds = new Set(incoming.map((row) => String(row._rowId)));
    const existingCount = this.tabulator
      .getRows()
      .filter((row) => String(row.getData()._rowGroup) === group).length;
    const hasNewRows = incoming.length > existingCount;

    // getRows() returns a snapshot array, so deleting while iterating is safe.
    this.tabulator.getRows().forEach((row) => {
      const data = row.getData();
      if (
        String(data._rowGroup) === group &&
        !incomingIds.has(String(data._rowId))
      ) {
        row.delete();
      }
    });

    // When new rows were added and the table has no sorter, updateOrAddData
    // appends them at the end of the group — the source-table order is lost.
    // Fall back to replaceData so the server-rendered order is authoritative.
    if (hasNewRows && this.tabulator.getSorters().length === 0) {
      this._parkGroupHeaders();
      this.containerTarget.style.minHeight = `${this.containerTarget.offsetHeight}px`;
      return this.tabulator
        .replaceData(this.extractRowData())
        .then(() => {
          this.applyFilters();
          this.flashRowGroup(group);
          return null;
        })
        .catch(() => {});
    }

    return this.tabulator
      .updateOrAddData(incoming)
      .then(() => {
        const sorters = this.tabulator.getSorters();
        if (sorters.length > 0) {
          this.tabulator.setSort(
            sorters.map((s) => ({ column: s.field, dir: s.dir }))
          );
        }
        this.applyFilters();
        // Flash last, after sort/filter have re-rendered the rows, so the
        // class lands on the elements that actually stay in the DOM.
        this.flashRowGroup(group);
        return null;
      })
      .catch(() => {});
  }

  // Keep the `_groupHeaders` cache current when turbo_stream replaces a
  // group header in-place. Without this, Tabulator's `generateElement` (called
  // on group expand) strips the new header and falls back to the stale cache.
  _observeGroupHeaders() {
    this._groupHeaderObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const key = node.getAttribute?.('data-group-header');
          if (key) this._groupHeaders.set(key, node);
        }
      }
    });
    this._groupHeaderObserver.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  // The header contents for one group: the server-rendered element the caller
  // parked in the hidden `groupHeaders` container.
  //
  // Tabulator appends whatever this returns into the group's header row, and
  // appending an already-attached node *moves* it — so there is only ever one
  // instance of the markup. That's what keeps the turbo frame inside it
  // targetable, its ids unique, and any typed-in field value intact.
  //
  // Query first so a turbo_stream that replaced the header wins; fall back to
  // the last node we saw for the moment during regeneration when Tabulator has
  // emptied the header row and the node is detached.
  groupHeaderFor(key) {
    const found = this.element.querySelector(
      `[data-group-header="${CSS.escape(key)}"]`
    );
    if (found) {
      this._groupHeaders.set(key, found);
      return found;
    }
    return this._groupHeaders.get(key) ?? key;
  }

  // Move every group header back into the hidden container so the next
  // rebuild (replaceData / redraw) re-queries them via `groupHeaderFor`
  // instead of falling back to the stale `_groupHeaders` cache. Without
  // this, a turbo_stream that replaced a header in-place inside a group row
  // is lost when that group row is destroyed during the rebuild.
  _parkGroupHeaders() {
    if (!this.hasGroupHeadersTarget) return;
    const container = this.groupHeadersTarget;
    this.element.querySelectorAll('[data-group-header]').forEach((header) => {
      if (!container.contains(header)) {
        container.appendChild(header);
      }
    });
  }

  // Redeclare the group set (and its display order) on a live grouped table.
  // Tabulator's `setGroupValues` re-runs its group setup and refreshes the
  // rows without touching filter state or the search input, so an added or
  // removed group doesn't cost the user their current narrowing.
  setGroupOrder(groupOrder) {
    if (!this.tabulator || !this.groupedRowsValue) return;
    // An empty array is a real order — every group has gone.
    if (!Array.isArray(groupOrder)) return;
    this.groupOrderValue = groupOrder.map(String);
    // Pin the container height so the page doesn't jump when setGroupValues
    // tears down and rebuilds the rows. Cleared on renderComplete.
    this.containerTarget.style.minHeight = `${this.containerTarget.offsetHeight}px`;
    this.tabulator.setGroupValues([this.groupOrderValue]);
  }

  // Briefly wash the freshly-synced rows in a light-blue background that fades
  // back to the default (CSS animation on `amba-tabulator-row-synced`, defined
  // in the component SCSS), so a save visibly points the user at the rows that
  // just changed.
  flashRowGroup(group) {
    this.tabulator.getRows().forEach((row) => {
      if (String(row.getData()._rowGroup) !== group) return;
      const el = row.getElement();
      if (!el) return;
      // Restart the animation when the same rows are synced again in quick
      // succession: remove the class, force a reflow, then re-add it.
      el.classList.remove('amba-tabulator-row-synced');
      el.getBoundingClientRect();
      el.classList.add('amba-tabulator-row-synced');
      // Drop the class once the wash finishes so it doesn't linger on the row.
      // A stale class would replay whenever Tabulator re-renders these rows
      // (e.g. the setSort/applyFilters of a later group's sync re-inserts the
      // element into the DOM, which restarts any CSS animation on it), causing
      // previously-updated groups to flash again on every followup save.
      el.addEventListener(
        'animationend',
        () => el.classList.remove('amba-tabulator-row-synced'),
        { once: true }
      );
    });
  }

  // Snapshot sort + filter + search + page into sessionStorage so the user
  // can navigate away and return without losing their place. No-op while
  // `_restoring` is true so the initial event echoes from `setSort` /
  // `setPage` / `applyFilters` don't fight the restore.
  saveState() {
    if (this._restoring) return;
    saveTableState(this.tableCaption, {
      sort: this.tabulator.getSorters().map((s) => ({
        column: s.field,
        dir: s.dir,
      })),
      filterState: serializeFilterState(this.filterState),
      search: this.hasSearchTarget ? this.searchTarget.value : '',
      page: this.paginationValue ? this.tabulator.getPage() : null,
    });
  }

  // Pull the last-saved snapshot back in. Order matters: filter state and
  // search go in first (so `applyFilters` produces the right row set), then
  // sort, then page (the page index is meaningless until both filter and
  // sort have settled).
  //
  // When the URL carries any deep-link filter params, persisted filter
  // state is dropped entirely — a deep-link is the user's explicit
  // current intent, and silently merging old narrowing on top would
  // produce a different (often empty) result set than the link promises.
  // Sort / page / search aren't part of the deep-link contract, so those
  // still rehydrate.
  restoreState() {
    try {
      const stored = loadTableState(this.tableCaption);
      if (!stored) return;

      if (this.remoteValue) {
        // Remote mode: state was pre-seeded into the initial config by
        // restoreStateIntoConfig(). Only refresh UI chrome — no
        // fetch-triggering API calls (setSort, setPage, applyFilters).
        refreshFilterButtonStates(this.containerTarget, this.filterState);
        refreshColumnSearchButtonStates(this.containerTarget, this.filterState);
        this.refreshClearButtonVisibility();
        return;
      }

      if (!this.urlHasDeepLinkParams()) {
        const persisted = deserializeFilterState(stored.filterState);
        // Merge at (field, kind) granularity: a URL deep-link may have
        // already seeded, say, a checkbox for a field — restore the persisted
        // search for that same field without clobbering the seeded checkbox.
        persisted.forEach((kinds, field) => {
          kinds.forEach((criteria, kind) => {
            if (!this.filterState.get(field)?.has(kind)) {
              this.setFilterCriteria(field, kind, criteria);
            }
          });
        });
      }
      if (stored.search && this.hasSearchTarget) {
        this.searchTarget.value = stored.search;
      }
      this.applyFilters();
      if (Array.isArray(stored.sort) && stored.sort.length > 0) {
        this.tabulator.setSort(stored.sort);
      }
      if (this.paginationValue && stored.page) {
        this.tabulator.setPage(stored.page);
      }
    } finally {
      this._restoring = false;
    }
  }

  // Pre-seed persisted state into instance properties that `remoteConfig`
  // reads, so the constructor's initial request carries the restored sort,
  // page, filter and search. Called from `initTable` before `new Tabulator`.
  // Remote mode only — local mode restores state post-construction via
  // `restoreState()` which is cheap (in-memory, no network).
  restoreStateIntoConfig() {
    const stored = loadTableState(this.tableCaption);
    if (!stored) return;

    if (!this.urlHasDeepLinkParams()) {
      const persisted = deserializeFilterState(stored.filterState);
      persisted.forEach((kinds, field) => {
        kinds.forEach((criteria, kind) => {
          if (!this.filterState.get(field)?.has(kind)) {
            this.setFilterCriteria(field, kind, criteria);
          }
        });
      });
    }

    if (stored.search && this.hasSearchTarget) {
      this.searchTarget.value = stored.search;
    }

    if (Array.isArray(stored.sort) && stored.sort.length > 0) {
      this._restoredSort = stored.sort;
    }

    if (this.paginationValue && stored.page) {
      this._restoredPage = stored.page;
    }
  }

  urlHasDeepLinkParams() {
    const params = new URL(window.location.href).searchParams;
    const columnKeys = this.columnsValue.some((col) => {
      const key = col.filter?.deepLinkKey;
      return key && params.has(key);
    });
    if (columnKeys) return true;
    // Server-only deep-link params (no matching column) — e.g.
    // `closed_by_user`. Their presence still means the user followed an
    // explicit drill-through, so persisted filters must be suppressed.
    return this.deepLinkKeysValue.some((key) => params.has(key));
  }

  get tableCaption() {
    return this.sourceTarget.getAttribute('aria-label') ?? 'table';
  }

  // Flattened list of leaf columns — groups (column hashes with a nested
  // `columns:` array) expand to their children. Used wherever we need
  // cell-positional iteration (extractCells, searchableFields) since
  // `<td>`s only correspond to leaves, not groups.
  get leafColumns() {
    if (!this._leafColumns) {
      const flatten = (cols) =>
        cols.flatMap((col) =>
          Array.isArray(col.columns) ? flatten(col.columns) : [col]
        );
      this._leafColumns = flatten(this.columnsValue);
    }
    return this._leafColumns;
  }

  // Fields the global search runs against. By convention a column is
  // "searchable" iff it declares a `sorter:` (i.e. it's a data column);
  // an explicit `searchable: true | false` on the column overrides that.
  // Cached after first computation since the column list doesn't change
  // mid-session.
  get searchableFields() {
    if (!this._searchableFields) {
      this._searchableFields = this.leafColumns
        .filter((col) => col.searchable ?? col.sorter !== undefined)
        .map((col) => col.field);
    }
    return this._searchableFields;
  }

  // Clear sorting & filters — bound to the toolbar button the component
  // auto-renders whenever the table has at least one clearable feature
  // (sortable column, filterable column, or `search: true`). Drops every
  // active sort, every column filter (popups + state Map), and the
  // global search input.
  //
  // We don't call Tabulator's `clearFilter` because we don't use its
  // built-in filter machinery — `applyFilters` rebuilds a single
  // function filter from `filterState` + the search input, so emptying
  // both and re-applying is what "no filter" means in our pipeline.
  clear() {
    if (this.hasSearchTarget) this.searchTarget.value = '';
    this.filterState.clear();
    // Dismiss any open column-search popup so it can't linger holding a
    // now-orphaned query (its state entry was just dropped from the Map).
    // A toolbar click already closes it via the outside-click handler, but
    // the cross-frame `tabulator-table:clear` event reaches `clear()` with
    // no mouse event — this keeps that path stale-free too. Reopening
    // rebuilds the input from the fresh (empty) state.
    closeColumnSearchPopup(this);
    // Cancel any pending debounced remote refetch (a keystroke in a remote
    // column search within the 200ms window). `applyFilters` below re-fetches
    // the now-cleared state once; without this the orphaned timer fires ~200ms
    // later and triggers a second, redundant AJAX request. No-op in local mode
    // (the timer is only ever set on the remote path). Mirrors `disconnect`.
    clearTimeout(this._applyFiltersTimer);

    if (this.remoteValue) {
      // Remote mode: `clearSort()` resets to page 1 and triggers a single
      // reload via the sort module. That reload picks up the now-cleared
      // filter/search state from `buildAjaxUrl`. Skip `applyFilters()` —
      // it would fire a second `setPage(1)` reload.
      this.tabulator.clearSort();
      refreshFilterButtonStates(this.containerTarget, this.filterState);
      refreshColumnSearchButtonStates(this.containerTarget, this.filterState);
      refreshSortLabels(this.containerTarget);
      this.refreshClearButtonVisibility();
      return;
    }

    this.tabulator.clearSort();
    this.applyFilters();
    refreshSortLabels(this.containerTarget);
    this.element.dispatchEvent(
      new CustomEvent('tabulator-table:cleared', { bubbles: false })
    );
  }

  // Toggle the clear button's `hidden` attribute based on whether
  // anything is currently filtering or sorting the table. Called from
  // `applyFilters` and the `dataSorted` event so any state change keeps
  // the button in step.
  refreshClearButtonVisibility() {
    const active =
      this.tabulator.getSorters().length > 0 || this.hasActiveNarrowing;

    if (this.hasClearTarget) this.clearTarget.hidden = !active;

    // Report state to a shell-rendered toolbar (org-group dashboards) so its
    // Clear button can show/hide in step. No-op for tables without a frame.
    if (this.frameId) {
      window.dispatchEvent(
        new CustomEvent('tabulator-table:state', {
          detail: { frame: this.frameId, active },
        })
      );
    }
  }

  // CSV export — bound to the `Download CSV` button rendered when
  // `csv_export: true`.
  //
  // Local mode: Tabulator's `DownloadModule` serialises the in-memory
  // rows (respecting active sort + filters), wraps them in a Blob, and
  // triggers a download via a synthetic anchor click.
  //
  // Remote mode: there are no in-memory rows to serialise — the table
  // only ever holds the current page. Instead, navigate to the same
  // endpoint with `.csv` and the current sort/filter state, and let the
  // server stream the full filtered result set.
  downloadCsv() {
    if (this.remoteValue) {
      // Remote CSV is a real `.csv` request, so the server audits it itself.
      window.location.href = this.buildCsvUrl();
      return;
    }
    const filename = `${slugify(this.tableCaption)}-${todayFilenameDate()}.csv`;

    // Group-split tables export a flat list of detail rows: Tabulator's
    // default range is the visible display list (collapsed children are
    // dropped) and includes group/summary rows, so pass a custom range that
    // flattens the rows and keeps only detail rows. See `csvDetailRows` in
    // csv_group_split.js. Gated on the `csv_group_header` opt-in — other tree
    // tables (e.g. alert outcomes) keep the default export, where parent rows
    // are the primary content.
    if (this.tabulator.options.dataTree && this.csvGroupHeaderValue) {
      this.tabulator.download('csv', filename, {}, () =>
        csvDetailRows(this.tabulator)
      );
    } else {
      this.tabulator.download('csv', filename);
    }
    this.auditCsvExport();
  }

  // Local CSV is generated in the browser, so there's no request to audit.
  // When an audit path is configured, ping it (fire-and-forget) so the export
  // is still recorded. A failed audit must never break the download.
  auditCsvExport() {
    if (!this.hasCsvAuditPathValue || !this.csvAuditPathValue) return;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.csvAuditPathValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': token, 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    }).catch(() => {});
  }

  // Build a `.csv` URL that mirrors what `buildAjaxUrl` would emit for
  // JSON, but without pagination — the CSV endpoint streams the entire
  // filtered + sorted result set.
  buildCsvUrl() {
    // Mirror `buildAjaxUrl`: derive the path from the resolved endpoint (so
    // an explicit `ajax_path:` is honoured — e.g. multi-table pages whose
    // data lives at a route other than the page's own path), then swap the
    // extension to `.csv`. Query params still come from the current page URL.
    const url = new URL(window.location.href);
    url.pathname = new URL(
      this.resolvedAjaxPath,
      window.location.origin
    ).pathname.replace(/(\.\w+)?$/, '.csv');

    this.stripSeededDeepLinkParams(url);

    url.searchParams.delete('page');
    url.searchParams.delete('page_size');

    url.searchParams.delete('q[s][]');
    this.tabulator.getSorters().forEach((s) => {
      const sortField = this.sortFieldFor(s.field);
      url.searchParams.append('q[s][]', `${sortField} ${s.dir}`);
    });

    url.searchParams.delete('search');
    const searchValue = this.hasSearchTarget
      ? this.searchTarget.value.trim()
      : '';
    if (searchValue) url.searchParams.set('search', searchValue);

    this.appendFilterParams(url);

    return url.toString();
  }

  // Drop the raw deep-link query params that were seeded into `filterState`
  // at construction. JS owns those filters from then on (emitting ransack
  // q-params), so the raw key must never ride along on a request — otherwise
  // it double-filters and, crucially, survives a Clear (which empties
  // `filterState` but not which keys we seeded). Keys we couldn't seed aren't
  // in `seededDeepLinkKeys`, so they pass through to the server untouched.
  stripSeededDeepLinkParams(url) {
    this.seededDeepLinkKeys.forEach((key) => url.searchParams.delete(key));
  }

  get config() {
    const config = this.remoteValue ? this.remoteConfig : this.localConfig;
    const modal = this.element.closest('.modal');
    // Tabulator mounts header popups (filter, column search) on
    // `document.body`. Inside a Bootstrap modal that puts them outside the
    // dialog's focus trap, which pulls focus back to the modal the moment the
    // user clicks into a popup input — the column search box can never be
    // typed in. Mounting them on the modal element keeps them inside the trap
    // while leaving positioning to Tabulator, which measures against whatever
    // container it is given.
    if (modal) config.popupContainer = modal;
    return config;
  }

  get localConfig() {
    const controller = this;
    const data = this.extractRowData();
    const hasChildren = data.some(
      (row) => Array.isArray(row._children) && row._children.length > 0
    );
    const footerData = this._footerData;

    // Build Tabulator's column config recursively so column groups
    // (column hashes with a nested `columns:` array) come through as
    // multi-row headers. Group entries pass their `title` and child
    // `columns` straight through; leaves get the full
    // sorter/filter/CSV/footer transformation.
    const buildColumn = (col) => {
      if (Array.isArray(col.columns)) {
        return {
          title: col.title,
          columns: col.columns.map(buildColumn),
        };
      }
      // Our `maxWidth:` API means a content-measurement cap — it stops an
      // outlier from blowing a column out during sizing, but doesn't prevent
      // fill-phase stretching into spare container space. Tabulator's native
      // `maxWidth` option behaves as a hard clamp on every setWidth call, so
      // we rename it to `contentCap` before Tabulator sees the column config;
      // the custom contentFill layout reads `contentCap` and applies it in
      // phase 1 only.
      //
      // `filter:` is our filter config (e.g. `{ type: "checkbox" }`).
      // `buildFilterColumnOptions` translates it into Tabulator's
      // `headerPopup` + `headerPopupIcon` options; we then drop `filter`
      // before Tabulator sees it.
      //
      // `sortField` is our remote-mode column → ransack sort field
      // mapping; Tabulator doesn't know it and warns if we hand it
      // through. `maxWidth` we re-shape into `contentCap` below.
      // `filter` we translate into Tabulator's popup options below.
      // `download` we rename to `downloadOverride` below. Strip all of
      // them from `rest` so Tabulator only sees keys it understands.
      const rest = stripColumnKeys(col, [
        'maxWidth',
        'filter',
        'sortField',
        'download',
        // Our per-column search opt-in — consumed by `installColumnSearchButtons`
        // (header button + popup), never a Tabulator column option.
        'columnSearch',
        // Our global-search opt-in, read off `columnsValue` by
        // `searchableFields` — Tabulator has no such option and warns on it.
        'searchable',
      ]);
      return {
        ...rest,
        formatter: col.formatter ?? 'html',
        // A column is sortable iff it declares a `sorter`. Without one,
        // explicitly disable sorting so Tabulator doesn't fall back to its
        // default string sorter.
        ...(col.sorter ? {} : { headerSort: false }),
        // Our "maxWidth" semantic: cap content measurement. Callers can set
        // `maxWidth: false` (or 0) to uncap. Default 300.
        contentCap: col.maxWidth === undefined ? 300 : col.maxWidth,
        // CSV export: action / non-data columns conventionally omit
        // `sorter:`, so default `download: false` for those — keeps Edit
        // buttons, link cells, etc. out of the exported file. Callers can
        // override either way with an explicit `download: true | false`.
        download: col.download ?? col.sorter !== undefined,
        // Filter button + popup. Presence of `filter:` is what makes a column
        // filterable (same opt-in style as `sorter:`). Tabulator's Popup
        // module handles button insertion, click-to-open, and close-on-blur.
        ...(col.filter
          ? buildFilterColumnOptions(col, col.filter, controller)
          : {}),
        // Footer row: when the source haml emits `<tr data-footer-row>`,
        // each cell becomes a static `bottomCalc` for its column. Ruby
        // computes the cell content (totals, averages, labels, whatever);
        // Tabulator just renders it column-aligned beneath the data rows.
        ...(footerData
          ? { bottomCalc: () => footerData[col.field] ?? '' }
          : {}),
      };
    };
    let columns = this.columnsValue.map(buildColumn);

    // CSV-only group split (org-group dashboards): prepend a hidden group
    // column and retitle the first column for the download. See csv_group_split.js.
    if (this.csvGroupHeaderValue) {
      columns = withGroupColumn(columns, {
        groupHeader: this.csvGroupHeaderValue,
        detailHeader: this.csvDetailHeaderValue || undefined,
      });
    }

    const config = {
      data,
      columns,
      // Stable row identity for surgical updates. `extractRowData` sets
      // `_hasRowIndex` when the source rows carry `data-row-id`; without it
      // Tabulator keeps its default auto-increment key (unchanged for every
      // table that doesn't opt in).
      ...(this._hasRowIndex ? { index: '_rowId' } : {}),
      columnDefaults: {
        // Tristate sorting (asc → desc → remove) is always on for sortable columns.
        headerSortTristate: true,
        // Opt-in per-table via the `vertical_centre_cells` Ruby attribute.
        // Without this, cells align to the top of the row — so any row with
        // a tall cell leaves the single-line cells stranded at the top of
        // the band.
        ...(this.verticalCentreCellsValue ? { vertAlign: 'middle' } : {}),
        // CSV export: cell values are pre-rendered HTML (formatter: 'html'
        // preserves links/badges/components), so a raw download would
        // serialise the markup. Run every cell through `extractCellText`
        // for download to get the visible text — strips `<time>`, `<a>`,
        // wrapper spans, etc. Per-column overrides (e.g. a column that
        // should export the `data-sort-value` instead of the visible text)
        // can supply their own `accessorDownload` and it'll win.
        accessorDownload(value) {
          return extractCellText(value);
        },
        // Show a tooltip with the cell's full text **only when the content is
        // actually truncated** (content wider than the cell). Returning undefined
        // suppresses the tooltip — always-on tooltips are noisy when most cells
        // already fit their content fully.
        tooltip(_e, cell) {
          const el = cell.getElement();
          if (el.scrollWidth > el.clientWidth) {
            return el.textContent.trim();
          }
        },
      },
      // Sort clicks only fire on the arrow icon, not the whole header. Keeps
      // the rest of the <th> available for other controls (filter buttons etc.)
      // and makes the click target explicit.
      rowFormatter: treeRowFormatter,
      headerSortClickElement: 'icon',
      // Custom layout (defined in tabulator_table/layout.js): content-sized
      // columns that fill any extra container width proportionally, and
      // scroll horizontally when the container is narrower than the total
      // content width.
      layout: 'contentFill',
      placeholder:
        this.emptyMessageValue || t('js.tabulator_table.controller.no_data'),
      langs: tabulatorLangs(),
      movableColumns: false,
      // Row reordering, opt-in per table via `movable_rows:`. The column that
      // declares `row_handle: true` becomes the drag handle.
      movableRows: this.movableRowsValue,
      // Optional starting sort, expressed in ransack `"field dir"` shape on
      // the Ruby side and translated to Tabulator's `[{column, dir}]` by
      // `initialSorters`. `undefined` when no default — Tabulator treats
      // an empty array as "first column ascending", which we don't want.
      initialSort: this.initialSorters,
    };

    if (this.paginationValue) {
      config.pagination = true;
      config.paginationMode = 'local';
      config.paginationSize = this.pageSizeValue;
      config.paginationCounter = 'rows';
    }

    // Nested rows are opt-in by data: only enable Tabulator's data tree when
    // a parent row actually has children (extractRowData populates `_children`
    // from `<tr data-child-row>` markers in the source). Avoids reserving the
    // toggle column on tables that never use the feature.
    if (hasChildren) {
      config.dataTree = true;
      // Start collapsed — users opt in to the extra detail by clicking.
      config.dataTreeStartExpanded = false;
      // Hide the L-branch lines that Tabulator draws by default — our
      // children share the parent's column structure (it's not a hierarchy
      // visually, just a disclosure), so the branches add noise.
      config.dataTreeBranchElement = false;
      // Children share the parent's column shape (per the design intent
      // above) so don't add per-level indent to their first cell. The
      // matching CSS reserves toggle-width on every first cell uniformly
      // so parent text, child text, and toggle-less parents all align.
      config.dataTreeChildIndent = 0;
      // Use real `<button>` elements for the toggle: Tabulator clones the
      // template onto each parent row, so we get native focus, native
      // Enter/Space activation, `:focus-visible` styling, and the `button`
      // role for free — no manual ARIA, no synthetic keydown listener.
      config.dataTreeCollapseElement = treeCollapseButtonHtml();
      config.dataTreeExpandElement = treeExpandButtonHtml();
      // The CSV-only group column is prepended and hidden, so pin the tree
      // toggle to the first real column — otherwise it lands on the hidden one.
      if (this.csvGroupHeaderValue) {
        config.dataTreeElementColumn = firstColumnField(this.columnsValue);
      }
    }

    // Row grouping (opt-in via `grouped_rows:`). `_rowGroup` is the same
    // logical group `data-row-group` already declares for turbo_stream syncs —
    // Tabulator accepts a bare field name with no matching column, so one
    // attribute serves both grouping and mutation sync.
    //
    // `groupStartOpen` (default true) and `groupToggleElement` (default
    // "arrow") are deliberately left alone: the arrow-only toggle is what keeps
    // clicks on buttons and forms inside the injected header from being
    // hijacked by the group's collapse handler.
    // A declared group order is enough on its own: a table whose groups are all
    // empty still has groups to show (and act on). Without one, grouping needs
    // at least one row that declares a group, or it would group on `undefined`.
    if (
      this.groupedRowsValue &&
      (this._hasRowGroup || this.groupOrderValue.length > 0)
    ) {
      config.groupBy = '_rowGroup';
      if (this.groupOrderValue.length) {
        config.groupValues = [this.groupOrderValue.map(String)];
      }
      config.groupHeader = (key) => this.groupHeaderFor(String(key));
    }

    return config;
  }

  // Remote-mode config. The server handles sort + pagination state; the
  // controller's job is to (a) describe each column the way Tabulator
  // expects, and (b) tell Tabulator how to build a request URL when it
  // needs the next page.
  //
  // Local-only Tabulator concerns (data tree, footer rows, csv export
  // accessors) all derive from the source `<table>` markup, which
  // doesn't exist here — so they're skipped entirely rather than
  // configured as no-ops.
  get remoteConfig() {
    const controller = this;
    const columns = this.columnsValue.map((col) => {
      // Same key-stripping logic as localConfig — keep `maxWidth`,
      // `filter`, and `sortField` off Tabulator's column definitions
      // (it doesn't know them and warns if we hand them through). The
      // originals stay on `this.columnsValue` for `sortFieldFor` etc.
      const rest = stripColumnKeys(col, [
        'maxWidth',
        'filter',
        'sortField',
        'columnSearch',
        'searchable',
      ]);
      return {
        ...rest,
        formatter: col.formatter ?? 'html',
        ...(col.sorter ? {} : { headerSort: col.headerSort ?? false }),
        contentCap: col.maxWidth === undefined ? 300 : col.maxWidth,
        // Remote rows skip CSV export entirely — exporting only the
        // current page would be misleading, and exporting all pages
        // requires a separate server endpoint. Drop `download` so
        // Tabulator's csv path can't accidentally include this column.
        download: false,
        ...(col.filter
          ? buildFilterColumnOptions(col, col.filter, controller)
          : {}),
      };
    });

    return {
      columns,
      columnDefaults: {
        headerSortTristate: true,
        // Same truncation tooltip as local mode — visible only when the
        // cell actually overflows.
        tooltip(_e, cell) {
          const el = cell.getElement();
          if (el.scrollWidth > el.clientWidth) {
            return el.textContent.trim();
          }
        },
      },
      headerSortClickElement: 'icon',
      layout: 'contentFill',
      placeholder:
        this.emptyMessageValue || t('js.tabulator_table.controller.no_data'),
      langs: tabulatorLangs(),
      movableColumns: false,
      // Pagination is mandatory in remote mode — without it Tabulator
      // expects a flat array response, but our endpoint always returns
      // `{ data, last_page }`. The Stimulus `pagination` value defaults
      // to false (matching local-mode default) and there's no clean way
      // to differentiate "explicitly false" from "not set" with a
      // Boolean value, so we just hard-set true here.
      pagination: true,
      paginationMode: 'remote',
      paginationSize: this.pageSizeValue,
      paginationInitialPage: this._restoredPage ?? 1,
      paginationCounter: 'rows',
      sortMode: 'remote',
      ajaxURL: this.resolvedAjaxPath,
      ajaxConfig: {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
      // Use a custom loading element. Function form so each load gets a
      // fresh clone of the template content. Falls back to Tabulator's
      // own default text if no template target is present.
      dataLoaderLoading: () => {
        if (!this.hasLoaderTarget) return null;
        const wrapper = document.createElement('div');
        wrapper.appendChild(this.loaderTarget.content.cloneNode(true));
        return wrapper;
      },
      // Build the URL on every request from current state. Tabulator
      // passes its standard params (page, size, sorters, filters); we
      // reshape into the ransack format the Rails side expects so the
      // JSON branch can stay a normal index action.
      ajaxURLGenerator: (_url, _config, params) => this.buildAjaxUrl(params),
      // Server response is `{ data: [...], last_page: N }` — pass through.
      // An `error` key means the endpoint had already committed its response
      // when the query failed, so it could only report the failure in the body.
      // Reject, or Tabulator renders the empty `data` as a genuine result and
      // the user reads a failure as "there is nothing here".
      ajaxResponse: (_url, _params, response) => {
        if (response && response.error) {
          throw new Error(`Table data request failed: ${response.error}`);
        }
        return response;
      },
      // Replaces Tabulator's bare "Error" default for a failed data load.
      dataLoaderError: t('js.tabulator_table.controller.data_load_error'),
      dataLoaderErrorTimeout: DATA_LOAD_ERROR_TIMEOUT_MS,
      initialSort: this._restoredSort ?? this.initialSorters,
    };
  }

  // Default endpoint path when no explicit `ajax_path:` was set: the
  // current page's path with `.json` appended. Same controller serves
  // the HTML and JSON branches, so consumers don't have to plumb a path
  // through.
  get resolvedAjaxPath() {
    if (this.ajaxPathValue) return this.ajaxPathValue;
    return window.location.pathname.replace(/(\.\w+)?$/, '.json');
  }

  // Translate the component's `default_sort` (ransack `"field dir"`
  // shape) into Tabulator's `initialSort` array. Empty string when no
  // default — Tabulator treats `[]` as "first column ascending", which
  // we don't want, so guard with `undefined`.
  get initialSorters() {
    if (!this.defaultSortValue) return undefined;
    const [field, dir] = this.defaultSortValue.split(/\s+/);
    if (!field) return undefined;
    return [{ column: field, dir: dir === 'desc' ? 'desc' : 'asc' }];
  }

  // Build the request URL from current page state. The mental model:
  //   "the page URL describes the constraints; the table layers
  //    interactive state (page / sort / filter) on top."
  //
  // Anything in the URL bar (date range, deep-link filter ids, floor
  // selection) round-trips automatically without anyone touching the
  // component config. The page never updates window.location during
  // interactions, so the URL we read here is whatever brought the user
  // to the page in the first place.
  buildAjaxUrl(tabulatorParams) {
    // Start from the resolved endpoint path, but pull query params from
    // the current page URL — that way an explicit `ajax_path` override
    // still picks up the deep-link constraints automatically.
    const url = new URL(window.location.href);
    url.pathname = new URL(
      this.resolvedAjaxPath,
      window.location.origin
    ).pathname;

    // Strip deep-link filter params we seeded into `filterState` — from that
    // point on, JS filter state is the source of truth and emits ransack
    // q-params below; leaving the raw key would double-filter and prevent the
    // user widening their selection (including via Clear, which empties
    // `filterState`). For columns we couldn't seed (e.g. sentinel values like
    // "none" that don't map to a checkbox option), the deep-link key stays on
    // the URL so the server's filter path handles it.
    this.stripSeededDeepLinkParams(url);

    // Single-value params we manage: `set` replaces any existing value.
    url.searchParams.set('page', tabulatorParams.page ?? 1);
    url.searchParams.set(
      'page_size',
      tabulatorParams.size ?? this.pageSizeValue
    );

    // Array-style params we manage: `append` would pile new values on
    // top of any inherited from the URL — drop them first so each
    // request carries only the current state.
    //
    // Tabulator's `ajaxURLGenerator` passes the current sort under
    // `params.sort` (singular), not `params.sorters` — getting this
    // wrong silently drops every sort interaction since `.sorters`
    // would just resolve to undefined.
    url.searchParams.delete('q[s][]');
    (tabulatorParams.sort ?? []).forEach((s) => {
      const sortField = this.sortFieldFor(s.field);
      url.searchParams.append('q[s][]', `${sortField} ${s.dir}`);
    });

    // Search box value (`search: true` on the component) — the server
    // applies free-text search across whatever columns it cares about.
    url.searchParams.delete('search');
    const searchValue = this.hasSearchTarget
      ? this.searchTarget.value.trim()
      : '';
    if (searchValue) url.searchParams.set('search', searchValue);

    this.appendFilterParams(url);

    return url.toString();
  }

  // Translate the controller's per-column `filterState` Map into ransack
  // params on the request URL. Each column's predicate becomes one or more
  // ransack query params shaped by the filter kind. Checkbox / number read
  // `filter.predicate` ("closed_by_id_in", "response_minutes"); column
  // search reads `columnSearch.predicate` ("name_cont"). Resolved against
  // `leafColumns` so a predicate on a grouped column's leaf is still found.
  appendFilterParams(url) {
    for (const [field, state] of this.eachFilterCriteria()) {
      const col = this.leafColumns.find((c) => c.field === field);
      if (!col) continue;

      if (state.kind === 'checkbox') {
        const predicate = col.filter?.predicate;
        if (predicate) this.appendCheckboxFilterParams(url, predicate, state);
      } else if (state.kind === 'number') {
        const predicate = col.filter?.predicate;
        if (predicate) this.appendNumberFilterParams(url, predicate, state);
      } else if (state.kind === 'search') {
        const predicate = col.columnSearch?.predicate;
        if (predicate) this.appendSearchFilterParams(url, predicate, state);
      }
    }
  }

  // Checkbox filters use the ransack `_in` array predicate, one entry
  // per checked option (e.g. `q[closed_by_id_in][]=26`). A non-narrowing
  // state (every option checked) emits nothing.
  appendCheckboxFilterParams(url, predicate, state) {
    // Drop any stale value for this predicate before re-emitting so a
    // deep-link `?q[<predicate>][]=42` doesn't pile up alongside the
    // current popup state on each request.
    url.searchParams.delete(`q[${predicate}][]`);

    const isNarrowing = state.checkedValues.size !== state.allOptions.length;
    if (!isNarrowing) return;

    if (state.checkedValues.size === 0) {
      // Every option deselected — user means "match nothing in this
      // column." Ransack treats an empty `_in` array as "no filter
      // applied" (full result set), the opposite of intent. Sidestep
      // with an always-false condition: `id < 0` is never true for
      // standard Rails models with positive IDs, so any consumer of
      // remote-mode Tabulator filters gets the empty-when-all-deselected
      // behaviour for free without per-controller bookkeeping.
      url.searchParams.set('q[id_lt]', '0');
    } else {
      state.checkedValues.forEach((value) => {
        url.searchParams.append(`q[${predicate}][]`, value);
      });
    }
  }

  // Number filters translate the popup's `{operator, value}` state into
  // the matching ransack scalar predicate (`<predicate>_eq`, `_gt`,
  // `_gteq`, `_lt`, `_lteq`, `_not_eq`) or `_null=true|false` for the
  // empty / not-empty operators.
  //
  // All possible variants of the predicate are stripped before re-
  // emitting so switching operators (e.g. `>` to `<`) replaces the
  // old condition instead of stacking alongside it.
  appendNumberFilterParams(url, predicate, state) {
    const operatorSuffixes = {
      '=': 'eq',
      '!=': 'not_eq',
      '>': 'gt',
      '>=': 'gteq',
      '<': 'lt',
      '<=': 'lteq',
    };

    Object.values(operatorSuffixes).forEach((suffix) => {
      url.searchParams.delete(`q[${predicate}_${suffix}]`);
    });
    url.searchParams.delete(`q[${predicate}_null]`);

    if (state.operator === 'empty') {
      url.searchParams.set(`q[${predicate}_null]`, 'true');
      return;
    }
    if (state.operator === 'not-empty') {
      url.searchParams.set(`q[${predicate}_null]`, 'false');
      return;
    }
    if (state.value === '') return; // value-bearing operator but no value yet

    const suffix = operatorSuffixes[state.operator];
    if (!suffix) return;
    url.searchParams.set(`q[${predicate}_${suffix}]`, state.value);
  }

  // Column search uses the ransack scalar `_cont` predicate (case-insensitive
  // substring — Postgres ILIKE), so the always-on match behaviour is the same
  // as local mode. The predicate is stripped before re-emitting so a stale
  // deep-link value or prior query doesn't stack, and a blank query emits
  // nothing — widening back to the full result set.
  appendSearchFilterParams(url, predicate, state) {
    url.searchParams.delete(`q[${predicate}]`);

    const query = state.query.trim();
    if (query === '') return;

    url.searchParams.set(`q[${predicate}]`, query);
  }

  // Resolve a column's data field to its server-side sort column. Most
  // columns sort on the same name they display (`closed_at` → ORDER BY
  // closed_at); a column can override via `sortField` in its config when
  // the display field doesn't map 1:1 to a sortable DB column.
  sortFieldFor(field) {
    const col = this.columnsValue.find((c) => c.field === field);
    return col?.sortField || field;
  }

  // Reads <tbody> rows from the hidden source table. Each cell's value is
  // the rendered HTML, so display (links, badges, components) is preserved.
  //
  // **Called once on connect.** Tabulator owns the row data after that —
  // re-rendering the table from updated server-side rows requires
  // re-mounting the controller (the `<table>` element itself is the
  // source of truth, so a Turbo frame / morph that replaces it triggers
  // disconnect/reconnect and we re-extract on the way in). If a
  // streaming use case ever appears, expose a `refresh()` action that
  // re-runs this and calls `tabulator.replaceData(...)`.
  //
  // If the <td> carries a `data-value` attribute, the HTML is wrapped in a
  // `<span data-sort-value="...">` — the overridden string/number sorters
  // read that attribute, so callers can display one thing and sort by
  // another (e.g. "High" displayed, "2" for sort order).
  //
  // Temporal columns (date/time/datetime) use `sorter: "temporal"` and
  // `UI::TimeComponent`: the temporal sorter reads the inner
  // `<time datetime="...">` attribute to get a chronologically-comparable
  // ISO string.
  //
  // Cell-to-column mapping is positional — column[i] gets cells[i] from
  // each row. Conditional `<td>`s in the haml block silently shift every
  // subsequent column's data, so we warn (once per row) when the lengths
  // disagree to surface the misalignment in dev/test.
  //
  // Nested rows: a `<tr data-child-row>` is treated as a child of the most
  // recent row one depth above it and pushed into that parent's `_children`
  // array (Tabulator's default child field). Depth defaults to 1; deeper
  // trees use `data-child-row="2"`, `"3"`, etc. The controller tracks a
  // parent stack indexed by depth, so any non-child row at depth N becomes
  // the prospective parent for the next row at depth N+1. Children share
  // the parent's column shape — same `<td>`s, same field mapping — so an
  // expanded child row visually slots into the same columns as the parent.
  extractRowData() {
    const rows = Array.from(this.sourceTarget.querySelectorAll('tbody tr'));
    const topLevel = [];
    // parentStack[N] is the most recent row at depth N. Top-level rows live
    // at depth 0; their children at depth 1; grandchildren at depth 2.
    const parentStack = [];
    // Reset on every call so we don't carry footer data over from a stale
    // run (e.g. a Turbo morph that replaces the source <table>).
    this._footerData = null;
    // Re-derived each pass from whether the source rows carry `data-row-id`.
    // Drives `index: '_rowId'` in the config and gates `syncRowGroup` — off
    // entirely for tables that don't opt into stable row identity.
    this._hasRowIndex = false;
    // Re-derived the same way: gates row grouping, so a table that opts in but
    // renders no grouped rows stays flat rather than grouping on `undefined`.
    this._hasRowGroup = false;
    rows.forEach((row) => {
      // Footer row: pre-computed in Ruby and pinned to the bottom of the
      // rendered table via `bottomCalc` per column. Siphoned out of the
      // data set entirely so it doesn't sort, filter, or page with the
      // rest of the rows. Use cases beyond totals (averages, summaries,
      // explanatory labels) all share this "always at the bottom, never
      // shuffled" shape, hence the generic name.
      if (row.dataset.footerRow !== undefined) {
        this._footerData = this.extractCells(row);
        return;
      }
      const rowData = this.extractCells(row);
      // Opt-in stable identity for surgical updates: `data-row-id` becomes the
      // Tabulator index (see `localConfig`) and `data-row-group` tags the row
      // with the logical group a turbo_stream can later re-sync as a unit (see
      // `syncRowGroup`).
      if (row.dataset.rowId !== undefined) {
        rowData._rowId = row.dataset.rowId;
        this._hasRowIndex = true;
      }
      if (row.dataset.rowGroup !== undefined) {
        rowData._rowGroup = row.dataset.rowGroup;
        this._hasRowGroup = true;
      }
      // `data-heading-row` marks a child row as a section heading inside the
      // children group — picked up by `rowFormatter` to add a class the SCSS
      // uses to span the heading text across the row.
      if (row.dataset.headingRow !== undefined) {
        rowData._heading = true;
      }
      // Surface row-level data attributes (e.g. `data-outcome-id`) to the
      // Tabulator-rendered `<tr>` via `rowFormatter`. Skip the keys we
      // consume internally so they don't leak back onto the rendered row.
      const internalKeys = new Set([
        'footerRow',
        'headingRow',
        'childRow',
        'rowId',
        'rowGroup',
      ]);
      const userDataAttrs = Object.fromEntries(
        Object.entries(row.dataset).filter(([k]) => !internalKeys.has(k))
      );
      if (Object.keys(userDataAttrs).length > 0) {
        rowData._dataAttrs = userDataAttrs;
      }
      let depth = 0;
      if (row.dataset.childRow !== undefined) {
        depth = parseInt(row.dataset.childRow, 10) || 1;
      }
      if (depth === 0) {
        topLevel.push(rowData);
      } else {
        const parent = parentStack[depth - 1];
        if (!parent) {
          console.warn(
            '[tabulator-table] child row appeared before any parent row — orphan rows are dropped from the table.',
            row
          );
          return;
        }
        (parent._children ||= []).push(rowData);
      }
      parentStack[depth] = rowData;
      parentStack.length = depth + 1; // discard any stale deeper parents
    });
    return topLevel;
  }

  // Pulls each `<td>`'s `innerHTML` into Tabulator's row data verbatim.
  // The source HTML is passed straight through to the cell formatter, so
  // anything user-supplied that ends up in a `<td>` must already be
  // escaped at the haml layer (Rails escapes by default).
  extractCells(row) {
    const cells = Array.from(row.querySelectorAll('td'));
    const leaves = this.leafColumns;
    if (cells.length !== leaves.length) {
      console.warn(
        `[tabulator-table] row has ${cells.length} cells but ${leaves.length} columns are configured — column data will be misaligned. Likely a conditional <td> in the haml block.`,
        row
      );
    }
    const rowData = {};
    leaves.forEach((col, i) => {
      const cell = cells[i];
      if (!cell) return;
      const html = cell.innerHTML.trim();
      // `data-value` feeds the string/number/temporal sort key (→ data-sort-value);
      // `data-filter-value` feeds the checkbox filter's option list + matcher
      // (→ data-filter-value). Neither reaches the CSV — the default
      // `accessorDownload` above exports each cell's visible text. A cell can
      // carry either attribute or both, so wrap when either is present and copy
      // across only the ones that are.
      if (
        cell.dataset.value !== undefined ||
        cell.dataset.filterValue !== undefined
      ) {
        const wrapper = document.createElement('span');
        if (cell.dataset.value !== undefined) {
          wrapper.setAttribute('data-sort-value', cell.dataset.value);
        }
        if (cell.dataset.filterValue !== undefined) {
          wrapper.setAttribute('data-filter-value', cell.dataset.filterValue);
        }
        wrapper.innerHTML = html;
        rowData[col.field] = wrapper.outerHTML;
      } else {
        rowData[col.field] = html;
      }
    });
    return rowData;
  }
}
